import castArray from 'lodash/castArray';
import entries from 'lodash/entries';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import isPlainObject from 'lodash/isPlainObject';
import memoize from 'lodash/memoize';
import startsWith from 'lodash/startsWith';
import toPath from 'lodash/toPath';

const resolveKeyPath = (keyPath, currentFormValuePath) => {
  let formValuePath = toPath(keyPath);

  if (startsWith(keyPath, '$') && currentFormValuePath) {
    formValuePath = formValuePath.reduce((acc, key) => {
      if (key === '$parent') {
        const parentKey = acc.splice(-1, 1);

        if (!isNaN(parentKey)) {
          acc.splice(-1, 1);
        }

        return [...acc];
      } else if (key === '$current') {
        return [...acc];
      } else {
        return [...acc, key];
      }
    }, toPath(currentFormValuePath));
  }

  return formValuePath;
};

export const reducei18n = (i18n, formValues, currentFormValuePath) =>
  i18n.reduce(function recurse(acc, node, index) {
    if (isArray(node)) {
      const rest = (isArray(node[1]) ? node[1] : [node[1]]).map(item =>
        isPlainObject(item)
          ? {
              ...item,
              _index: index,
            }
          : item
      );

      return [
        ...acc,
        ...(node[0].some(
          conditional =>
            recurse(
              [],
              {
                condition: conditional,
              },
              index
            ).length > 0
        )
          ? rest
          : []),
      ];
    }

    if (node.condition) {
      const { condition, ...rest } = node;

      if (isArray(condition)) {
        return [
          ...acc,
          ...(condition.some(
            conditional =>
              recurse([], {
                ...rest,
                condition: conditional,
              }).length > 0
          )
            ? [{ ...rest, _index: index }]
            : []),
        ];
      }

      return entries(condition).every(([keyPath, value]) =>
        castArray(value).includes(
          get(formValues, resolveKeyPath(keyPath, currentFormValuePath), null)
        )
      )
        ? [...acc, { ...rest, _index: index }]
        : acc;
    }

    return [...acc, { ...node, _index: index }];
  }, []);

export const resolvei18nConditions = memoize(
  function recurse(i18n, formValues) {
    if (isPlainObject(i18n)) {
      return entries(i18n).reduce(
        (acc, [key, value]) => ({
          ...acc,
          [key]: recurse(value, formValues),
        }),
        {}
      );
    }

    if (isArray(i18n)) {
      return reducei18n(i18n, formValues).map(value =>
        recurse(value, formValues)
      );
    }

    return i18n;
  },
  (_, formValues) => JSON.stringify(formValues)
);

export const getDependencyFieldsKeyPath = (i18n, currentFormValuePath) =>
  i18n.reduce(function recurse(acc, node) {
    if (isArray(node)) {
      return [...acc, ...recurse([], { condition: node[0] })];
    }

    if (node.condition) {
      const { condition } = node;

      if (isArray(condition)) {
        return [
          ...acc,
          ...condition.reduce(
            (acc, conditional) => [
              ...acc,
              ...recurse([], {
                condition: conditional,
              }),
            ],
            []
          ),
        ];
      }

      return [
        ...acc,
        ...entries(condition).map(([keyPath, _]) =>
          resolveKeyPath(keyPath, currentFormValuePath)
        ),
      ];
    }

    return acc;
  }, []);
